/** Provider for focus state — manages the active focus task and timer duration. */
// @ts-check
import { useState, useCallback, useEffect } from 'react';
import { FocusContext } from './FocusContextDef.js';

/**
 * Persists focus state to localStorage across sessions.
 */
export function FocusProvider({ children }) {
  const [activeFocusTaskId, setActiveFocusTaskId] = useState(
    () => localStorage.getItem('activeFocusTaskId') || null,
  );
  const [focusStartTime, setFocusStartTime] = useState(() => {
    const st = localStorage.getItem('focusStartTime');
    return st ? parseInt(st, 10) : null;
  });

  const startFocus = useCallback(
    /** @param {string} taskId */
    (taskId) => {
      const now = Date.now();
      setActiveFocusTaskId(taskId);
      setFocusStartTime(now);
      localStorage.setItem('activeFocusTaskId', taskId);
      localStorage.setItem('focusStartTime', now.toString());
    },
    [],
  );

  const stopFocus = useCallback(() => {
    setActiveFocusTaskId(null);
    setFocusStartTime(null);
    localStorage.removeItem('activeFocusTaskId');
    localStorage.removeItem('focusStartTime');
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      if (activeFocusTaskId) {
        stopFocus();
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [activeFocusTaskId, stopFocus]);

  const value =
    /** @type {{ activeFocusTaskId: string|null, focusStartTime: number|null, startFocus: (taskId: string) => void, stopFocus: () => void }} */ ({
      activeFocusTaskId,
      focusStartTime,
      startFocus,
      stopFocus,
    });

  return (
    <FocusContext.Provider value={value}>{children}</FocusContext.Provider>
  );
}
